import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { FeatureFlags, FeatureFlagType } from './featureFlag';
import type { ReleaseFlags, ReleaseFlagType } from './releaseFlag';

const shallowEqualReleaseFlags = (a: ReleaseFlags, b: ReleaseFlags): boolean => {
  const aKeys = Object.keys(a) as ReleaseFlagType[];
  const bKeys = Object.keys(b) as ReleaseFlagType[];
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
};

const shallowEqualFeatureFlags = (a: FeatureFlags, b: FeatureFlags): boolean => {
  const aKeys = Object.keys(a) as FeatureFlagType[];
  const bKeys = Object.keys(b) as FeatureFlagType[];
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
};

/**
 * ユーザー情報の型定義
 *
 * FSD準拠: Entitiesレイヤー間の横断的importを避けるため、
 * session独自の型として定義
 */
type UserRole = 'admin' | 'editor' | null;

type Tenant = {
  id: string;
  code: string;
  name: string;
  localGovernmentCode: string;
};

type User = {
  id: string;
  name: string;
  email: string;
  lastLogin: string | null;
  isFirstLoginCompleted: boolean;
  role: UserRole;
  tenant: Tenant;
};

type SessionState = {
  isAuthenticated: boolean;
  user: User | null;
  releaseFlags: ReleaseFlags;
  features: FeatureFlags;

  // Actions
  setAuthenticated: (isAuthenticated: boolean) => void;
  setUser: (user: User | null) => void;
  setReleaseFlags: (releaseFlags: ReleaseFlags) => void;
  setFeatures: (features: FeatureFlags) => void;
  clearSession: () => void;
};

/**
 * セッション状態管理ストア
 *
 * FSD Entities層の責務:
 * - 認証状態（isAuthenticated, user）の一元管理
 * - リリースフラグの保持
 * - Cookie と連動した状態更新
 * - ログアウト時のクリア処理
 */
export const useSessionStore = create<SessionState>()(
  devtools(
    (set) => ({
      isAuthenticated: false,
      user: null,
      releaseFlags: {},
      features: {},
      setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
      setUser: (user) => set({ user }),
      setReleaseFlags: (releaseFlags) =>
        set((state) =>
          shallowEqualReleaseFlags(state.releaseFlags, releaseFlags) ? state : { releaseFlags },
        ),
      setFeatures: (features) =>
        set((state) => (shallowEqualFeatureFlags(state.features, features) ? state : { features })),
      clearSession: () =>
        set({ isAuthenticated: false, user: null, releaseFlags: {}, features: {} }),
    }),
    { name: 'SessionStore' },
  ),
);
